// Binds the options form to Settings. Each field has a parser; a value that
// fails to parse marks the field invalid and leaves the setting untouched.

import {
  clampFilterBwHz,
  FILTER_BW_STEP_HZ,
  FILTER_ORDERS,
  MAX_FILTER_BW_HZ,
  MAX_REJECTION_DB,
  MIN_FILTER_BW_HZ,
  MIN_REJECTION_DB,
} from '../overlays/filter.ts'
import { LORA_BANDWIDTHS_HZ, LORA_CODING_RATES, LORA_SPREADING_FACTORS } from '../overlays/signal.ts'
import { PALETTE_NAMES, isPaletteName } from '../render/palette.ts'
import type { Settings } from '../state.ts'
import { isValidTimeZone } from '../tz.ts'
import { formatHz, parseDuration, parseFrequency, parseTimeInput, type TimeInput } from '../units.ts'

type Field = keyof Settings

interface Binding {
  field: Field
  parse: (text: string) => Settings[Field]
  format: (value: Settings[Field]) => string
}

const optional =
  <T>(parse: (text: string) => T) =>
  (text: string): T | null =>
    text.trim() === '' ? null : parse(text)

const number = (text: string): number => {
  const n = Number(text)
  if (!Number.isFinite(n)) throw new Error('not a number')
  return n
}

/** Positions of travel on each dB slider. Fine enough that a drag feels continuous. */
const DB_SLIDER_POSITIONS = 500

/** The dB span a slider can reach: the loaded run's own extremes. */
export interface DbRange {
  lo: number
  hi: number
}

interface DbSlider {
  field: 'dbMin' | 'dbMax'
  input: HTMLInputElement
  output: HTMLOutputElement
  /** The end of travel that means auto: the low end for the floor, the high end for the ceiling. */
  autoAt: 'lo' | 'hi'
}

/**
 * Decimals worth showing and storing for a given slider step.
 *
 * Both the readout and the stored value use this, so they cannot disagree: a
 * value rounded finer than the track would snap back to a different position
 * than the one it prints, and a value rounded coarser would print a number the
 * thumb is not actually on.
 */
const dbDecimals = (step: number): number => Math.min(4, Math.max(1, Math.ceil(-Math.log10(step))))

const formatTime = (value: TimeInput | null): string => {
  if (!value) return ''
  if (value.kind === 'epoch') return String(Math.round(value.ms / 1000))
  const n = value.naive
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${n.year}-${pad(n.month)}-${pad(n.day)} ${pad(n.hour)}:${pad(n.minute)}:${pad(n.second)}`
}

const formatHzInput = (hz: number | null): string => {
  if (hz === null) return ''
  if (hz === 0) return ''
  if (hz % 1e6 === 0) return `${hz / 1e6}M`
  if (hz % 1e3 === 0) return `${hz / 1e3}k`
  return String(hz)
}

const formatSeconds = (sec: number | null): string => {
  if (sec === null) return ''
  if (sec % 3600 === 0) return `${sec / 3600}h`
  if (sec % 60 === 0) return `${sec / 60}m`
  return `${sec}s`
}

/**
 * The fields of the signal check. Changing one with the check off switches it
 * on: describing a signal is asking to see it against the capture, and nothing
 * else in the app reads these.
 */
const CHECK_FIELDS = new Set<Field>([
  'signalBwHz',
  'signalSf',
  'signalCr',
  'payloadBytes',
  'thresholdMode',
  'thresholdMarginDb',
  'thresholdAbsDb',
  'fitLossBudgetDb',
])

const BINDINGS: Binding[] = [
  {
    field: 'palette',
    parse: (t) => (isPaletteName(t) ? t : 'charolastra'),
    format: (v) => String(v),
  },
  { field: 'lowHz', parse: optional(parseFrequency), format: (v) => formatHzInput(v as number | null) },
  { field: 'highHz', parse: optional(parseFrequency), format: (v) => formatHzInput(v as number | null) },
  {
    field: 'offsetHz',
    parse: (t) => (t.trim() === '' ? 0 : parseFrequency(t)),
    format: (v) => formatHzInput(v as number),
  },
  { field: 'begin', parse: optional(parseTimeInput), format: (v) => formatTime(v as TimeInput | null) },
  { field: 'end', parse: optional(parseTimeInput), format: (v) => formatTime(v as TimeInput | null) },
  { field: 'headSec', parse: optional(parseDuration), format: (v) => formatSeconds(v as number | null) },
  { field: 'tailSec', parse: optional(parseDuration), format: (v) => formatSeconds(v as number | null) },
  {
    field: 'compress',
    parse: (t) => {
      const n = t.trim() === '' ? 0 : number(t)
      if (n < 0) throw new Error('negative')
      return n
    },
    format: (v) => ((v as number) > 0 ? String(v) : ''),
  },
  { field: 'ytickSec', parse: optional(parseDuration), format: (v) => formatSeconds(v as number | null) },
  {
    field: 'timeDisplay',
    parse: (t) => (t === 'local' ? 'local' : 'utc'),
    format: (v) => String(v),
  },
  {
    field: 'captureZoneOverride',
    parse: (t) => {
      const zone = t.trim()
      if (zone === '') return null
      if (!isValidTimeZone(zone)) throw new Error('unknown zone')
      return zone
    },
    format: (v) => (v === null ? '' : String(v)),
  },
  {
    field: 'signalBwHz',
    parse: (t) => {
      const hz = number(t)
      if (!LORA_BANDWIDTHS_HZ.includes(hz)) throw new Error('not a LoRa bandwidth')
      return hz
    },
    format: (v) => String(v),
  },
  {
    field: 'signalSf',
    parse: (t) => {
      const sf = number(t)
      if (!LORA_SPREADING_FACTORS.includes(sf)) throw new Error('SF5 to SF12')
      return sf
    },
    format: (v) => String(v),
  },
  {
    field: 'signalCr',
    parse: (t) => {
      const cr = number(t)
      if (!LORA_CODING_RATES.includes(cr)) throw new Error('4/5 to 4/8')
      return cr
    },
    format: (v) => String(v),
  },
  {
    field: 'payloadBytes',
    parse: (t) => {
      const n = Math.round(number(t))
      if (n < 1 || n > 255) throw new Error('1 to 255 bytes')
      return n
    },
    format: (v) => String(v),
  },
  {
    field: 'thresholdMode',
    parse: (t) => (t === 'absolute' ? 'absolute' : 'relative'),
    format: (v) => String(v),
  },
  { field: 'thresholdMarginDb', parse: number, format: (v) => String(v) },
  { field: 'thresholdAbsDb', parse: number, format: (v) => String(v) },
  {
    field: 'fitLossBudgetDb',
    parse: (t) => {
      const n = number(t)
      if (n < 0.1 || n > MAX_REJECTION_DB) throw new Error(`0.1 to ${MAX_REJECTION_DB} dB`)
      return n
    },
    format: (v) => String(v),
  },
  {
    field: 'filterOrder',
    parse: (t) => {
      const n = number(t)
      if (!FILTER_ORDERS.includes(n as (typeof FILTER_ORDERS)[number])) throw new Error('not an offered order')
      return n
    },
    format: (v) => String(v),
  },
  {
    field: 'filterRejectionDb',
    parse: (t) => {
      const n = Math.round(number(t))
      if (n < MIN_REJECTION_DB || n > MAX_REJECTION_DB) throw new Error(`${MIN_REJECTION_DB} to ${MAX_REJECTION_DB} dB`)
      return n
    },
    format: (v) => String(v),
  },
]

/** The band a filter can be placed in, from the loaded run. */
export interface FilterRange {
  lowHz: number
  highHz: number
  /** One bin, the finest the center can usefully be moved by. */
  stepHz: number
}

export class Controls {
  readonly #form: HTMLFormElement
  readonly #inputs = new Map<Field, HTMLInputElement | HTMLSelectElement>()
  readonly #dbSliders: DbSlider[] = []
  #dbRange: DbRange | null = null
  #dbStep = 0
  #filterRange: FilterRange | null = null
  #filterCenter: HTMLInputElement | null = null
  #filterCenterOut: HTMLOutputElement | null = null
  #filterBw: HTMLInputElement | null = null
  #filterBwOut: HTMLOutputElement | null = null
  #filterOn: HTMLInputElement | null = null
  #checkOn: HTMLInputElement | null = null
  /** The settings last reflected into the form; the sliders read each other through it. */
  #settings: Settings | null = null

  constructor(form: HTMLFormElement, onChange: (patch: Partial<Settings>) => void) {
    this.#form = form
    const palette = form.elements.namedItem('palette') as HTMLSelectElement
    for (const name of PALETTE_NAMES) {
      const option = document.createElement('option')
      option.value = name
      option.textContent = name
      palette.append(option)
    }

    const fill = (name: string, values: readonly number[], label: (v: number) => string) => {
      const select = form.elements.namedItem(name) as HTMLSelectElement | null
      if (!select) return
      for (const value of values) {
        const option = document.createElement('option')
        option.value = String(value)
        option.textContent = label(value)
        select.append(option)
      }
    }
    fill('signalBwHz', LORA_BANDWIDTHS_HZ, (hz) => `${hz / 1e3} kHz`)
    fill('signalSf', LORA_SPREADING_FACTORS, (sf) => `SF${sf}`)
    fill('signalCr', LORA_CODING_RATES, (cr) => `4/${cr}`)
    fill('filterOrder', FILTER_ORDERS, (order) => `${order * 20} dB/decade`)

    for (const binding of BINDINGS) {
      const input = form.elements.namedItem(binding.field) as HTMLInputElement | HTMLSelectElement | null
      if (!input) continue
      this.#inputs.set(binding.field, input)
      input.addEventListener('change', () => {
        try {
          const value = binding.parse(input.value)
          input.classList.remove('invalid')
          if (binding.field === 'thresholdMode') this.#reflectThreshold(String(value))
          const wakes = CHECK_FIELDS.has(binding.field) && this.#checkOn && !this.#checkOn.checked
          onChange({ [binding.field]: value, ...(wakes ? this.#enableCheck() : {}) } as Partial<Settings>)
        } catch {
          input.classList.add('invalid')
        }
      })
    }

    for (const [field, autoAt] of [
      ['dbMin', 'lo'],
      ['dbMax', 'hi'],
    ] as const) {
      const input = form.elements.namedItem(field) as HTMLInputElement | null
      const output = document.getElementById(`${field === 'dbMin' ? 'db-min' : 'db-max'}-value`)
      if (!input || !(output instanceof HTMLOutputElement)) continue
      const slider: DbSlider = { field, input, output, autoAt }
      this.#dbSliders.push(slider)
      // 'input', not 'change': the heatmap follows the thumb while it moves.
      input.addEventListener('input', () => onChange(this.#dbPatch(slider)))
    }

    const byId = <T extends HTMLElement>(id: string, ctor: new () => T): T | null => {
      const el = document.getElementById(id)
      return el instanceof ctor ? el : null
    }
    this.#checkOn = byId('check-on', HTMLInputElement)
    this.#checkOn?.addEventListener('change', () => {
      const on = this.#checkOn!.checked
      this.#reflectCheckOn(on)
      onChange({ signalCheckOn: on })
    })

    this.#filterOn = byId('filter-on', HTMLInputElement)
    this.#filterCenter = byId('filter-center', HTMLInputElement)
    this.#filterCenterOut = byId('filter-center-value', HTMLOutputElement)
    this.#filterBw = byId('filter-bw', HTMLInputElement)
    this.#filterBwOut = byId('filter-bw-value', HTMLOutputElement)

    this.#filterOn?.addEventListener('change', () => {
      const on = this.#filterOn!.checked
      this.#reflectFilterOn(on)
      onChange({ filterOn: on })
    })
    this.#filterCenter?.addEventListener('input', () => {
      const hz = Number(this.#filterCenter!.value)
      // Turning a slider is asking for the filter, not just describing one.
      onChange({ filterCenterHz: hz, ...(this.#filterOn?.checked ? {} : this.#enableFilter()) })
    })
    this.#filterBw?.addEventListener('input', () => {
      const bw = clampFilterBwHz(Number(this.#filterBw!.value))
      onChange({ filterBwHz: bw, ...(this.#filterOn?.checked ? {} : this.#enableFilter()) })
    })

    form.addEventListener('submit', (event) => event.preventDefault())
  }

  /** Dragging a filter slider with the filter off switches it on rather than doing nothing. */
  #enableFilter(): Partial<Settings> {
    if (this.#filterOn) this.#filterOn.checked = true
    this.#reflectFilterOn(true)
    return { filterOn: true }
  }

  #reflectFilterOn(on: boolean): void {
    this.#form.classList.toggle('filter-off', !on)
  }

  #enableCheck(): Partial<Settings> {
    if (this.#checkOn) this.#checkOn.checked = true
    this.#reflectCheckOn(true)
    return { signalCheckOn: true }
  }

  #reflectCheckOn(on: boolean): void {
    this.#form.classList.toggle('check-off', !on)
  }

  /**
   * The setting a slider position means.
   *
   * The outer end of each slider's travel is auto: the value it would take
   * there is the data's own extreme anyway, so the end of the track is the
   * natural home for "follow the data". Auto differs from pinning that same
   * number only when the data changes, which is exactly when it should.
   *
   * The two sliders cannot cross. Pushing one past the other stops it a step
   * short, since an inverted or empty dB range has no rendering.
   */
  #dbPatch(slider: DbSlider): Partial<Settings> {
    const range = this.#dbRange
    if (!range) return {}
    const step = this.#dbStep
    const raw = Number(slider.input.value)
    const value = Number(raw.toFixed(dbDecimals(step)))

    if (slider.field === 'dbMin') {
      if (raw <= range.lo) return { dbMin: null }
      const ceiling = (this.#pinned('dbMax') ?? range.hi) - step
      const next = Math.min(value, ceiling)
      return { dbMin: next <= range.lo ? null : next }
    }
    if (raw >= range.hi) return { dbMax: null }
    const floor = (this.#pinned('dbMin') ?? range.lo) + step
    const next = Math.max(value, floor)
    return { dbMax: next >= range.hi ? null : next }
  }

  #pinned(field: 'dbMin' | 'dbMax'): number | null {
    return this.#settings?.[field] ?? null
  }

  /**
   * Points the dB sliders at the loaded run's own range, or disables them when
   * there is nothing loaded. Called on every settings change, so a value the
   * data cannot honor is corrected on the track as well as in the image.
   */
  setDbRange(range: DbRange | null, settings: Settings): void {
    this.#settings = settings
    if (!range || !Number.isFinite(range.lo) || !Number.isFinite(range.hi)) {
      this.#dbRange = null
      for (const slider of this.#dbSliders) {
        slider.input.disabled = true
        // Park the thumb on the end that means auto, so the empty track still
        // reads as what it will do once a capture is open.
        slider.input.value = slider.autoAt === 'lo' ? slider.input.min || '0' : slider.input.max || '100'
        slider.output.textContent = 'auto'
        slider.output.classList.add('auto')
      }
      return
    }

    // A run whose readings are all identical would give a zero-width track.
    const lo = range.hi > range.lo ? range.lo : range.lo - 0.5
    const hi = range.hi > range.lo ? range.hi : range.hi + 0.5
    this.#dbRange = { lo, hi }
    this.#dbStep = (hi - lo) / DB_SLIDER_POSITIONS
    const format = (db: number) => db.toFixed(dbDecimals(this.#dbStep))

    for (const slider of this.#dbSliders) {
      slider.input.disabled = false
      slider.input.min = String(lo)
      slider.input.max = String(hi)
      // A step that divides the span exactly keeps both ends reachable, which
      // is what makes the auto end selectable at all.
      slider.input.step = String(this.#dbStep)
      const pinned = settings[slider.field]
      const auto = pinned === null
      const end = slider.autoAt === 'lo' ? lo : hi
      const value = auto ? end : Math.min(Math.max(pinned, lo), hi)
      slider.input.value = String(value)
      slider.output.classList.toggle('auto', auto)
      slider.output.textContent = auto ? `auto (${format(end)})` : format(value)
      slider.input.title = auto
        ? `Following the data: ${format(end)} dB. Drag inward to pin a value.`
        : `Pinned to ${format(value)} dB. Drag to the ${slider.autoAt === 'lo' ? 'left' : 'right'} end for auto.`
    }
  }

  /**
   * Points the filter sliders at the loaded band, or disables them when there
   * is nothing loaded. The values shown are the resolved ones, so a center
   * carried over from another capture reads as the clamped value the plot is
   * actually drawing rather than the stale number in storage.
   */
  setFilterRange(range: FilterRange | null, centerHz: number, bwHz: number): void {
    this.#filterRange = range
    const center = this.#filterCenter
    const bw = this.#filterBw
    if (!center || !bw) return
    if (!range) {
      center.disabled = true
      bw.disabled = true
      if (this.#filterCenterOut) this.#filterCenterOut.textContent = '—'
      if (this.#filterBwOut) this.#filterBwOut.textContent = '—'
      return
    }
    center.disabled = false
    center.min = String(range.lowHz)
    center.max = String(range.highHz)
    center.step = String(range.stepHz)
    center.value = String(centerHz)
    center.title = `Passband center. The capture covers ${formatHz(range.lowHz)} to ${formatHz(range.highHz)}.`
    if (this.#filterCenterOut) this.#filterCenterOut.textContent = formatHz(centerHz)

    bw.disabled = false
    bw.min = String(MIN_FILTER_BW_HZ)
    bw.max = String(MAX_FILTER_BW_HZ)
    bw.step = String(FILTER_BW_STEP_HZ)
    bw.value = String(bwHz)
    bw.title = `The -3 dB width, ${formatHz(MIN_FILTER_BW_HZ)} to ${formatHz(MAX_FILTER_BW_HZ)} in whole MHz.`
    if (this.#filterBwOut) this.#filterBwOut.textContent = formatHz(bwHz)
  }

  /**
   * Shows what the loaded data covers on a field, and flags a value the data
   * cannot honor. The typed text is left alone: rewriting it would hide the
   * mistake rather than point at it.
   */
  hint(field: Field, placeholder: string, outOfRange: boolean, title: string): void {
    const input = this.#inputs.get(field)
    if (!input) return
    if (input instanceof HTMLInputElement) input.placeholder = placeholder
    input.title = title
    input.classList.toggle('clamped', outOfRange)
  }

  /** Shows the margin field for a relative threshold, or the level for absolute. */
  #reflectThreshold(mode: string): void {
    this.#form.classList.toggle('absolute-threshold', mode === 'absolute')
  }

  /** Reflects settings into the fields without firing change events. */
  render(settings: Settings): void {
    for (const binding of BINDINGS) {
      const input = this.#inputs.get(binding.field)
      if (!input) continue
      input.value = binding.format(settings[binding.field])
      input.classList.remove('invalid', 'clamped')
    }
    this.#reflectThreshold(settings.thresholdMode)
    if (this.#filterOn) this.#filterOn.checked = settings.filterOn
    this.#reflectFilterOn(settings.filterOn)
    if (this.#checkOn) this.#checkOn.checked = settings.signalCheckOn
    this.#reflectCheckOn(settings.signalCheckOn)
    this.setDbRange(this.#dbRange, settings)
  }

  get form(): HTMLFormElement {
    return this.#form
  }
}
